import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Box, Typography, Chip, CircularProgress } from '@material-ui/core';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { setPois, setPoisLoading, addWaypoint } from '../../store/slices/routesSlice';
import { setHighlightedPoiId, setNeedsRecalculation, selectUI } from '../../store/slices/uiSlice';
import { TYPE_MAP, fetchPoisAlongRoute } from '../../utils/overpassApi';


const EMPTY = [];

const FILTER_TYPES = [
  { key: 'all',          labelPt: 'Todos',              labelEn: 'All',            emoji: '📍' },
  { key: 'fuelStation',  labelPt: 'Combustível',        labelEn: 'Fuel',           emoji: '⛽' },
  { key: 'services',     labelPt: 'Áreas de Serviço',   labelEn: 'Service Areas',  emoji: '⛽' },
  { key: 'restArea',     labelPt: 'Descanso',           labelEn: 'Rest Areas',     emoji: '😴' },
  { key: 'truckParking', labelPt: 'Parque Camiões',     labelEn: 'Truck Parking',  emoji: '🅿' },
  { key: 'hotel',        labelPt: 'Hotel / Motel',      labelEn: 'Hotel / Motel',  emoji: '🏨' },
];

const selectSheetData = state => {
  const r = state.routes.current;
  return {
    pois: r.pois || EMPTY,
    poisLoading: r.poisLoading,
    hasRoute: !!r.computedRoute,
    routeCoords: r.computedRoute?.geometry?.coordinates || null,
  };
};

function RouteServicesSheet({ mapRef }) {
  const [open, setOpen] = useState(false);
  const [sheetHeight, setSheetHeight] = useState(220);
  const [resizingSheet, setResizingSheet] = useState(false);
  const sheetStartRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [fetchError, setFetchError] = useState(false);
  const dispatch = useDispatch();
  const { pois, poisLoading, hasRoute, routeCoords } = useSelector(selectSheetData, shallowEqual);
  const { lang, highlightedPoiId } = useSelector(selectUI);
  const isPt = lang === 'pt';

  const hasPois = pois.length > 0;
  const needsFetch = !hasPois && !poisLoading && !fetchError;

  const handleOpen = useCallback(() => {
    setOpen(o => {
      const opening = !o;
      // Fetch POIs on first open if not yet loaded
      if (opening && needsFetch && routeCoords && routeCoords.length > 1) {
        setFetchError(false);
        dispatch(setPoisLoading(true));
        fetchPoisAlongRoute(routeCoords)
          .then(result => dispatch(setPois(result)))
          .catch(() => setFetchError(true))
          .finally(() => dispatch(setPoisLoading(false)));
      }
      return opening;
    });
  }, [needsFetch, routeCoords, dispatch]);

  const onPoiClick = useCallback((poiId) => {
    dispatch(setHighlightedPoiId(highlightedPoiId === poiId ? null : poiId));
  }, [dispatch, highlightedPoiId]);

  const handleAddToRoute = useCallback((poi, e) => {
    e.stopPropagation();
    dispatch(addWaypoint({ name: poi.name, coordinates: poi.coordinates }));
    dispatch(setNeedsRecalculation(true));
    dispatch(setHighlightedPoiId(null));
  }, [dispatch]);

  const hoverTimerRef = useRef(null);
  const savedViewRef = useRef(null);

  const handlePoiHover = useCallback((poi) => {
    const map = mapRef?.current;
    if (!map || !poi?.coordinates) return;
    if (!savedViewRef.current) {
      savedViewRef.current = { center: map.getCenter().toArray(), zoom: map.getZoom() };
    }
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    map.flyTo({ center: poi.coordinates, zoom: Math.max(map.getZoom(), 13), duration: 600 });
    hoverTimerRef.current = setTimeout(() => {
      if (savedViewRef.current && map) {
        map.flyTo({ center: savedViewRef.current.center, zoom: savedViewRef.current.zoom, duration: 800 });
        savedViewRef.current = null;
      }
    }, 2000);
  }, [mapRef]);

  const handlePoiLeave = useCallback(() => {
    // Cancel timer and return immediately
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    const map = mapRef?.current;
    if (savedViewRef.current && map) {
      map.flyTo({
        center: savedViewRef.current.center,
        zoom: savedViewRef.current.zoom,
        duration: 600,
      });
      savedViewRef.current = null;
    }
  }, [mapRef]);

  const handleRetry = useCallback(() => {
    if (!routeCoords || routeCoords.length < 2) return;
    setFetchError(false);
    dispatch(setPoisLoading(true));
    fetchPoisAlongRoute(routeCoords)
      .then(result => dispatch(setPois(result)))
      .catch(() => setFetchError(true))
      .finally(() => dispatch(setPoisLoading(false)));
  }, [routeCoords, dispatch]);

  const filteredPois = useMemo(() => {
    if (activeFilter === 'all') return pois;
    return pois.filter(p => p.type === activeFilter);
  }, [pois, activeFilter]);

  const counts = useMemo(() => {
    const c = {};
    for (const p of pois) {
      c[p.type] = (c[p.type] || 0) + 1;
    }
    c.all = pois.length;
    return c;
  }, [pois]);

  // Sheet resize via drag on top edge
  const handleSheetResizeStart = useCallback((e) => {
    e.preventDefault();
    sheetStartRef.current = { y: e.clientY, h: sheetHeight };
    setResizingSheet(true);
    function onMove(ev) {
      const delta = sheetStartRef.current.y - ev.clientY;
      const newH = Math.max(150, Math.min(500, sheetStartRef.current.h + delta));
      setSheetHeight(newH);
    }
    function onUp() {
      setResizingSheet(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'row-resize';
  }, [sheetHeight]);

  if (!hasRoute) return null;

  return (
    <>
      {/* Pull-up handle */}
      <Box
        onClick={handleOpen}
        style={{
          position: 'absolute',
          bottom: open ? 200 : 6,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 120,
          width: 36,
          height: 20,
          borderRadius: 10,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 1px 6px rgba(0,0,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'bottom 0.35s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#5f6368"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          }}
        >
          <polyline points="7 14 12 9 17 14" />
        </svg>
      </Box>

      {/* Bottom sheet */}
      <Box
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: open ? sheetHeight : 0,
          zIndex: 110,
          background: 'rgba(255,255,255,0.97)',
          backdropFilter: 'blur(16px)',
          borderRadius: '16px 16px 0 0',
          boxShadow: open ? '0 -4px 24px rgba(0,0,0,0.12)' : 'none',
          overflow: 'hidden',
          transition: 'height 0.35s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Resize handle */}
        <Box
          onMouseDown={handleSheetResizeStart}
          style={{
            height: 6, cursor: 'row-resize', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Box style={{ width: 36, height: 3, borderRadius: 2, background: '#cbd5e1' }} />
        </Box>
        {/* Header */}
        <Box style={{ padding: '14px 18px 8px', flexShrink: 0 }}>
          <Typography style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>
            {isPt ? 'Serviços na Rota' : 'Route Services'}
            {hasPois && (
              <span style={{ fontSize: '0.72rem', fontWeight: 400, color: '#94a3b8', marginLeft: 8 }}>
                {pois.length} {isPt ? 'encontrados' : 'found'}
              </span>
            )}
          </Typography>
        </Box>

        {/* Loading state */}
        {poisLoading && (
          <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 12, padding: 20 }}>
            <CircularProgress size={28} style={{ color: '#1d3f58' }} />
            <Typography style={{ fontSize: '0.75rem', color: '#64748b', textAlign: 'center' }}>
              {isPt ? 'A procurar serviços ao longo da rota...' : 'Searching services along the route...'}
            </Typography>
            <Typography style={{ fontSize: '0.62rem', color: '#94a3b8', textAlign: 'center' }}>
              {isPt ? 'Isto pode demorar alguns segundos' : 'This may take a few seconds'}
            </Typography>
          </Box>
        )}

        {/* Error state */}
        {!poisLoading && fetchError && (
          <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 10, padding: 20 }}>
            <Typography style={{ fontSize: '0.75rem', color: '#ef4444' }}>
              {isPt ? 'Não foi possível carregar os serviços' : 'Could not load services'}
            </Typography>
            <Box
              onClick={handleRetry}
              style={{
                padding: '6px 16px', borderRadius: 8, cursor: 'pointer',
                background: '#1d3f58', color: '#fff', fontSize: '0.7rem', fontWeight: 600,
              }}
            >
              {isPt ? 'Tentar novamente' : 'Try again'}
            </Box>
          </Box>
        )}

        {/* Empty state (fetch done, no results) */}
        {!poisLoading && !fetchError && !hasPois && (
          <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: 20 }}>
            <Typography style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
              {isPt ? 'Nenhum serviço encontrado na rota' : 'No services found along the route'}
            </Typography>
          </Box>
        )}

        {/* Filter chips + POI list (only when loaded) */}
        {hasPois && !poisLoading && (<>
          <Box style={{ padding: '0 18px 10px', display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
            {FILTER_TYPES.map(f => {
              const count = counts[f.key] || 0;
              if (f.key !== 'all' && count === 0) return null;
              const isActive = activeFilter === f.key;
              return (
                <Chip
                  key={f.key}
                  label={`${f.emoji} ${isPt ? f.labelPt : f.labelEn} (${count})`}
                  size="small"
                  onClick={() => setActiveFilter(f.key)}
                  style={{
                    fontSize: '0.68rem',
                    fontWeight: isActive ? 700 : 500,
                    height: 26,
                    background: isActive ? '#1d3f58' : 'rgba(0,0,0,0.05)',
                    color: isActive ? '#fff' : '#475569',
                    border: isActive ? 'none' : '1px solid rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                />
              );
            })}
          </Box>

          <Box style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
            {filteredPois.map(poi => {
              const cfg = TYPE_MAP[poi.type] || { emoji: '📍', color: '#64748b', labelPt: poi.type, labelEn: poi.type };
              const isHighlighted = highlightedPoiId === poi.id;
              return (
                <React.Fragment key={poi.id}>
                <Box
                  onClick={() => onPoiClick(poi.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 10,
                    marginBottom: 4,
                    cursor: 'pointer',
                    background: isHighlighted ? `${cfg.color}15` : 'transparent',
                    border: isHighlighted ? `1px solid ${cfg.color}40` : '1px solid transparent',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { if (!isHighlighted) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; handlePoiHover(poi); }}
                  onMouseLeave={e => { if (!isHighlighted) e.currentTarget.style.background = 'transparent'; handlePoiLeave(); }}
                >
                  <Box style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    background: `${cfg.color}18`,
                    border: `2px solid ${cfg.color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    flexShrink: 0,
                  }}>
                    {cfg.emoji}
                  </Box>
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Typography style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#1e293b',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {poi.name}
                    </Typography>
                    <Typography style={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                      {isPt ? cfg.labelPt : cfg.labelEn}
                      {poi.tags?.brand ? ` · ${poi.tags.brand}` : ''}
                      {poi.tags?.opening_hours ? ` · ${poi.tags.opening_hours}` : ''}
                    </Typography>
                  </Box>
                  {/* Distance info */}
                  <Box style={{ textAlign: 'right', flexShrink: 0, minWidth: 52 }}>
                    {poi.kmAlongRoute != null && (
                      <Typography style={{ fontSize: '0.68rem', fontWeight: 600, color: '#475569', lineHeight: 1.2 }}>
                        km {poi.kmAlongRoute}
                      </Typography>
                    )}
                    {poi.distKm != null && (
                      <Typography style={{ fontSize: '0.58rem', color: '#94a3b8', lineHeight: 1.2 }}>
                        {poi.distKm < 1 ? `${Math.round(poi.distKm * 1000)}m` : `${poi.distKm}km`} {isPt ? 'da rota' : 'off route'}
                      </Typography>
                    )}
                  </Box>
                  {/* Add to route button */}
                  {isHighlighted && (
                    <Box
                      onClick={(e) => handleAddToRoute(poi, e)}
                      style={{
                        padding: '4px 10px', borderRadius: 8, background: '#1d3f58',
                        color: '#fff', fontSize: '0.62rem', fontWeight: 600, cursor: 'pointer',
                        whiteSpace: 'nowrap', flexShrink: 0, transition: 'background 0.15s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#2a5a7a'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#1d3f58'; }}
                    >
                      + {isPt ? 'Rota' : 'Route'}
                    </Box>
                  )}
                </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </>)}
      </Box>
    </>
  );
}

export default RouteServicesSheet;
